import { useState, useRef, useEffect } from "react";
import { Download, CreditCard, HelpCircle, LogOut, ChevronDown, Sparkles, User } from "lucide-react";

const planDisplay = {
  free: { label: "Free Plan", color: "bg-slate-100 text-slate-600" },
  paid: { label: "Studio Pass 2.0", color: "bg-amber-100 text-amber-700" },
  agency: { label: "Agency Plan", color: "bg-purple-100 text-purple-700" },
};

export function ProfileDropdown({ user, isAdmin, displayName, onLogout, onUpgrade, onDownloadsTab, onBillingClick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const plan = user?.agency_plan ? "agency" : user?.plan === "paid" ? "paid" : "free";
  const { label, color } = planDisplay[plan];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        data-testid="profile-trigger"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
          <User className="h-4 w-4 text-slate-500" />
        </div>
        <span className="hidden sm:inline text-sm font-medium text-slate-600">{displayName}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          data-testid="profile-dropdown"
          className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150 z-50"
        >
          {/* User summary */}
          <div className="px-4 pt-4 pb-3 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-800 truncate" data-testid="profile-phone">{displayName}</p>
            {isAdmin && <p className="text-[11px] text-orange-600 font-medium mt-0.5">Admin</p>}
            <span className={`inline-block mt-2 text-[11px] font-semibold px-2.5 py-1 rounded-full ${color}`} data-testid="profile-plan-badge">
              {label}
            </span>
          </div>

          {/* Upgrade section — for users who can upgrade */}
          {(plan === "free" || user?.plan_id === "starter" || user?.plan_id === "growth" || (user?.plan_id === "scale" && (user?.downloads_used ?? 0) >= (user?.free_download_limit ?? 999))) && (
            <div className="px-2 py-2 border-b border-slate-100">
              <button
                data-testid="profile-upgrade-btn"
                onClick={() => { setOpen(false); onUpgrade(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
              >
                <Sparkles className="h-4 w-4" />
                {user?.plan_id === "scale" ? "Need More Downloads?" : plan === "free" ? "Upgrade to Studio Pass 2.0" : "Upgrade Plan"}
              </button>
            </div>
          )}

          {/* Menu items */}
          <div className="py-1.5 px-2">
            <MenuItem icon={Download} label="My Downloads" testId="profile-downloads-btn" onClick={() => { setOpen(false); onDownloadsTab(); }} />
            <MenuItem icon={CreditCard} label="Billing / Plans" testId="profile-billing-btn" onClick={() => { setOpen(false); if (onBillingClick) onBillingClick(); }} />
            <MenuItem icon={HelpCircle} label="Help / Support" testId="profile-help-btn" onClick={() => { setOpen(false); window.open("mailto:support@studiopass.in", "_blank"); }} />
          </div>

          {/* Logout */}
          <div className="border-t border-slate-100 py-1.5 px-2">
            <MenuItem icon={LogOut} label="Log out" testId="profile-logout-btn" onClick={() => { setOpen(false); onLogout(); }} danger />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
        danger
          ? "text-red-500 hover:bg-red-50"
          : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
